import React, { useMemo, useState, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import SalesFilters from './SalesFilters'; // Assuming SalesFilters is in the same directory
import { DownloadIcon } from 'lucide-react';

function ProjectBreakdownTab({ project, allCustomerSales }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedConfig, setSelectedConfig] = useState('all');
    const [selectedBuilding, setSelectedBuilding] = useState('all');
    const [projectFilter, setProjectFilter] = useState('all');
    const gridRef = useRef(null);

console.log("---",allCustomerSales);
    const projectSalesData = useMemo(() => {
        if (!project || !allCustomerSales) {
            return [];
        }

        let filteredSales = allCustomerSales.filter(sale => sale?.customerSale?.propertyData?.projectPhase.project?.projectId === project.projectId);

        if (searchTerm) {
            filteredSales = filteredSales.filter(sale =>
                sale?.customerSale?.propertyData?.projectPhase.project?.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (selectedConfig !== 'all') {
            filteredSales = filteredSales.filter(sale => sale?.propertyConfigData?.configName === selectedConfig);
        }

        if (selectedBuilding !== 'all') {
            filteredSales = filteredSales.filter(sale => sale?.buildingData?.buildingName === selectedBuilding);
        }

        if (projectFilter !== 'all') {
            filteredSales = filteredSales.filter(sale => sale?.customerSale?.propertyData?.projectPhase.project?.projectName === projectFilter);
        }

        return filteredSales;

    }, [project, allCustomerSales, searchTerm, selectedConfig, selectedBuilding, projectFilter]);



    const exportToCsv = useCallback(() => {
        if (!gridRef.current || !gridRef.current.api) {
            console.warn("Grid API not available.");
            return;
        }

        const csvExportParams = {
            fileName: 'project_breakdown.csv',
            columnKeys: ['configName', 'buildingName', 'unitsSold', 'totalSalesValue'] // Specify which columns to export
        };

        gridRef.current.api.exportDataAsCsv(csvExportParams);
    }, []);


    // Breakdown Data
    const breakdownData = useMemo(() => {
        if (!projectSalesData || projectSalesData.length === 0) {
            return [];
        }

        const aggregatedData = {};

        projectSalesData.forEach(sale => {
            const configName = sale?.propertyConfigData?.configName || 'N/A';
            const buildingName = sale?.buildingData?.buildingName || 'N/A';
            const key = `${configName}-${buildingName}`;

            if (!aggregatedData[key]) {
                aggregatedData[key] = {
                    configName: configName,
                    buildingName: buildingName,
                    unitsSold: 0,
                    totalSalesValue: 0,
                };
            }

            aggregatedData[key].unitsSold++;
            aggregatedData[key].totalSalesValue += sale?.customerSale?.basePrice || 0;
        });

        return Object.values(aggregatedData);
    }, [projectSalesData]);

    const columnDefs = useMemo(() => [
        { field: 'configName', headerName: 'Configuration', flex: 1 },
        { field: 'buildingName', headerName: 'Building', flex: 1 },
        { field: 'unitsSold', headerName: 'Units Sold', valueFormatter: params => params.value.toLocaleString(), flex: 1 },
        { field: 'totalSalesValue', headerName: 'Total Sales Value (₹)', valueFormatter: params => '₹' + params.value.toLocaleString(), flex: 1 },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 100 // Optional: Set a minimum width to prevent columns from becoming too narrow
    }), []);

    // Extract unique values for filters
    const uniqueConfigs = useMemo(() => {
        const configs = new Set(['all']); // Include 'all' as an option
        allCustomerSales.forEach(sale => {
            const configName = sale?.propertyConfigData?.configName;
            if (configName) {
                configs.add(configName);
            }
        });
        return Array.from(configs);
    }, [allCustomerSales]);

    const uniqueBuildings = useMemo(() => {
        const buildings = new Set(['all']); // Include 'all' as an option
        allCustomerSales.forEach(sale => {
            const buildingName = sale?.buildingData?.buildingName;
            if (buildingName) {
                buildings.add(buildingName);
            }
        });
        return Array.from(buildings);
    }, [allCustomerSales]);

    const uniqueProjects = useMemo(() => {
        const projects = new Set(['all']); // Include 'all' as an option
        allCustomerSales.forEach(sale => {
            const projectName = sale?.customerSale?.propertyData?.projectPhase.project?.projectName;
            if (projectName) {
                projects.add(projectName);
            }
        });
        return Array.from(projects);
    }, [allCustomerSales]);

    return (
        <div >

            <div className='flex gap-2'>
                <SalesFilters
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedConfig={selectedConfig}
                    setSelectedConfig={setSelectedConfig}
                    selectedBuilding={selectedBuilding}
                    setSelectedBuilding={setSelectedBuilding}
                    uniqueConfigs={uniqueConfigs}
                    uniqueBuildings={uniqueBuildings}
                    showSearchTerm={true}
                    showConfigFilter={true}
                    showBuildingFilter={true}
                    showDateRange={false}



                />
                <button className="flex items-center gap-2 font-medium text-sm text-white bg-green-600 hover:bg-green-700 p-2 rounded shadow-md" onClick={exportToCsv}>
                    <DownloadIcon size={18} /> Export
                </button>
            </div>


            <div className="ag-theme-quartz font-normal mt-4" style={{ height: 400, width: '100%' }}>
                <AgGridReact
                    ref={gridRef}  // Attach the ref to AgGridReact
                    rowData={breakdownData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    domLayout='autoHeight' // Optional: Adjusts the grid height to fit the content
                />
            </div>
        </div>
    );
}

export default ProjectBreakdownTab;